import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchIncidentAnalysisData, fetchIncidentAnalysisDataExcel } from '../../../../../../../utils/api/reportAPI';
import IncidentAnalysisTable from './Table/IncidentAnalysisTable';
import ReportFooter from '../ReportsFooter/ReportFooter';
import PieChart from './Chart/PieChart';
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import toasters from '../../../../../../../utils/toasters/Toasters';
import { useSearchParams } from 'react-router-dom'

const formatPayloadDates = (payload) => {
    if (!payload) return payload;

    const formattedPayload = { ...payload };

    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }

    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }

    return formattedPayload;
};

const IncidentAnalysis = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [payload, setPayload] = useState(null);
    const [exportType, setExportType] = useState('N'); // State to manage export type

        useEffect(() => {

        // Try reading from URL params
        const startDate = searchParams.get('startDate')
        const endDate = searchParams.get('endDate')
        const code = searchParams.get('code')

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code }
            setPayload(urlPayload)
            sessionStorage.setItem('incidentPayload', JSON.stringify(urlPayload))
            return
        }

        // fallback to location state (existing behavior)
        if (location.state) {
            setPayload(location.state)
            sessionStorage.setItem('incidentPayload', JSON.stringify(location.state))
            return
        }

        // fallback to sessionStorage
        const storedPayload = sessionStorage.getItem('incidentPayload')
        if (storedPayload) {
            setPayload(JSON.parse(storedPayload))
        }

    }, [location.state, searchParams])

    const formattedPayload = formatPayloadDates(payload);

    const getIncidentAnalysisData = useCallback(async () => {
        return fetchIncidentAnalysisData({ ...formattedPayload, export: exportType });
    }, [formattedPayload, exportType]);

    const {
        isLoading,
        data: incidentAnalysisData,
        isError,
        error,
      } = useQuery(
        ['fetch-incident-analysis', formattedPayload, exportType],
        getIncidentAnalysisData,
        {
          enabled: !!formattedPayload, 
          retry: (failureCount, error) => {
            const status = error?.response?.status;
            return failureCount < 1 && status >= 400 && status < 600; 
          },
          retryDelay: 1000, 
        }
      );
      

    const labels = incidentAnalysisData?.map((item) => item.shortName || item.name) || [];
    const counts = incidentAnalysisData?.map((item) => item.count) || [];

    const downloadPDF = () => {
        const doc = new jsPDF();

        doc.setFontSize(18);
        doc.text('Incident Analysis Report', 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(
            `Report Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
            14,
            32
        );

        const tableColumn = ['Types', 'Count'];
        const tableRows = [];

        incidentAnalysisData?.forEach((item) => {
            const rowData = [item.name, item.count];
            tableRows.push(rowData);
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 40,
        });

        doc.save('incident-analysis-report.pdf');
    };

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' });

        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };

    const ExcelIncidentAnalysis = async () => {
        try {
            const response = await fetchIncidentAnalysisDataExcel({ ...formattedPayload, export: 'Y' });
            downloadFile(response, 'Incident Analysis');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <>
                    <div className="mb-4">
                        <IncidentAnalysisTable
                            isLoading={isLoading}
                            data={incidentAnalysisData}
                            displayDate={formattedPayload}
                        />
                    </div>
                    <div>
                        <h5 className="mb-3">Incidents</h5>
                            <Row className="justify-content-center">
                                <Col xs={12} sm={10} md={8} lg={12}>
                                    <Card className="shadow-sm">
                                        <Card.Body>
                                            <div
                                                className="d-flex justify-content-center align-items-center w-100"
                                                style={{ height: '400px' }}
                                            >
                                                <PieChart
                                                    labels={labels}
                                                    counts={counts}
                                                />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                    </div>
                    <ReportFooter
                        BackText={'Back'}
                        PdfText={'PDF'}
                        MailText={'Mail'}
                        ExcelText={'Excel'}
                        onPdfClick={downloadPDF}
                        onExcelClick={ExcelIncidentAnalysis}
                        reportData={formattedPayload}
                        reportCode={payload?.code}
                        reportTableData={incidentAnalysisData}
                        generatePDFFunction={downloadPDF}
                    />
                </>
            )}
        </>
    );
};

export default IncidentAnalysis;